import { Overlay, OverlayConfig, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    OnDestroy,
    Output,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { FuiDestroyService } from '@services/destroy.service';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-popup-content',
    templateUrl: './popup-content.component.html',
    imports: [OverlayModule],
    providers: [FuiDestroyService],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})

/** Компонент всплывающего контента */
export class PopupContentComponent implements OnDestroy {
    readonly #destroy$ = inject(FuiDestroyService);

    /** Шаблон для добавления в портал */
    readonly #viewContainerRef: ViewContainerRef = inject(ViewContainerRef);

    /** CDK Оверлей */
    readonly #overlay: Overlay = inject(Overlay);

    /** Ссылка на CDK Оверлей */
    private overlayRef: OverlayRef | null = null;

    /** Ссылка на шаблон */
    @ViewChild('content', { static: true }) content: TemplateRef<unknown>;

    /**
     * Меню/попап закрыт (backdrop или hide)
     */
    @Output() readonly closed = new EventEmitter<void>();

    /** Отобразить контент (координаты начала отображения)*/
    show(x: number, y: number): void {
        this.destroyOverlay(false);
        this.createOverlay(x, y);
    }

    /** Скрыть контент */
    hide(): void {
        this.destroyOverlay(true);
    }

    /** Уничтожение компонента */
    ngOnDestroy(): void {
        this.destroyOverlay(false);
    }

    /** Создание оверлея */
    private createOverlay(x: number, y: number): void {
        const positionStrategy = this.#overlay
            .position()
            .flexibleConnectedTo({ x, y } as { x: number; y: number })
            .withPositions([
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'top',
                },
            ]);

        const overlayConfig = new OverlayConfig({
            positionStrategy,
            scrollStrategy: this.#overlay.scrollStrategies.reposition(),
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
        });

        this.overlayRef = this.#overlay.create(overlayConfig);

        const templatePortal = new TemplatePortal(this.content, this.#viewContainerRef);

        this.overlayRef.attach(templatePortal);

        this.overlayRef
            .backdropClick()
            .pipe(takeUntil(this.#destroy$))
            .subscribe(() => this.hide());
    }

    /**
     * Уничтожение оверлея
     * @param emitClosed Сообщить о закрытии
     */
    private destroyOverlay(emitClosed: boolean): void {
        if (!this.overlayRef) {
            return;
        }

        this.overlayRef.dispose();
        this.overlayRef = null;

        if (emitClosed) {
            this.closed.emit();
        }
    }
}
